/**
 * S18 — live knob controls for the active view's `knob` declarations. Number
 * knobs render as a numeric input (min/max/step), enum knobs as a dropdown; the
 * value flows through the ActiveView context into the projection as an instance
 * binding (drives `$knob` / `within $knob`). Hidden when the active view has no
 * knobs. Element/`from` knobs fall back to a text input (dynamic-value pickers
 * ride the same seam later).
 */
import type { ReactNode } from "react";
import type { Value } from "@archlang/engine";
import { useActiveView } from "../workbench/ActiveViewContext.js";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useEffectiveViewName } from "./useEffectiveViewName.js";
import { findView, instanceParentOf } from "./viewRepr.js";

function valueStr(v: Value): string {
  if (v.kind === "number") return String(v.value);
  if (v.kind === "string" || v.kind === "identifier") return v.text;
  // A dotted element binding (`el: Payments.Api` → ref) displays as its
  // joined path — the same string the live element-knob input holds.
  if (v.kind === "ref") return v.path.join(".");
  return "";
}

export function KnobPicker(): ReactNode {
  const { resolvedModel } = useWorkspaceModel();
  const { draftView, knobsByView, setKnob } = useActiveView();
  // Resolve the view being RENDERED, not the HUD picker's raw pick: a view
  // TAB's own view (via the effective name, qualified-first) — a builder
  // draft wins over both, same precedence as the projection. Without this a
  // view opened on its own tab never showed its knobs.
  const effectiveViewName = useEffectiveViewName();
  const view = draftView ?? findView(resolvedModel?.views, effectiveViewName);
  // Knob DECLS live on the PARENT view; an instance carries only bindings —
  // resolve the parent's decls so an active instance still shows its knobs
  // (its own bindings become the displayed defaults, overriding the decl's).
  // instanceParentOf = the projection's own parent resolution — findView's
  // qualified-first pick could land on a different twin than the rendered
  // merge, hiding a knob the board actively honors (r36).
  const parent = instanceParentOf(view, resolvedModel?.views);
  const knobs = (parent ?? view)?.header.knobs ?? [];
  const bound = new Map(
    (view?.bindings ?? []).map((b) => [b.name, valueStr(b.value)] as const),
  );
  // Values are PER-VIEW (same keying as the projection: drafts under ::draft)
  // so a same-named knob on another view never bleeds in.
  const viewKey = draftView ? "::draft" : effectiveViewName;
  const knobValues = (viewKey ? knobsByView[viewKey] : undefined) ?? {};
  const set = (name: string, value: number | string) => {
    if (viewKey) setKnob(viewKey, name, value);
  };
  if (knobs.length === 0) return null;
  return (
    <div className="arch-knob-picker" role="group" aria-label="View knobs">
      {knobs.map((k) => (
        <label key={k.name} className="arch-knob">
          <span className="arch-knob-name">{k.name}</span>
          {k.kind === "number" ? (
            <input
              type="number"
              aria-label={k.name}
              min={k.min}
              max={k.max}
              step={k.step}
              value={String(knobValues[k.name] ?? bound.get(k.name) ?? (k.default.kind === "number" ? k.default.value : ""))}
              onChange={(e) => set(k.name, Number(e.target.value))}
            />
          ) : k.kind === "enum" ? (
            (() => {
              const cur = String(
                knobValues[k.name] ?? bound.get(k.name) ?? valueStr(k.values[0] ?? ({ kind: "string", text: "" } as Value)),
              );
              const declared = k.values.map(valueStr);
              return (
                <select
                  aria-label={k.name}
                  value={cur}
                  onChange={(e) => {
                    // Preserve the DECLARED value's kind: a number enum value must
                    // flow as a number (the <select> yields strings) — a string
                    // "3" binds as string-kind and breaks `within $knob`.
                    const s = e.target.value;
                    const match = k.values.find((v) => valueStr(v) === s);
                    set(k.name, match?.kind === "number" ? match.value : s);
                  }}
                >
                  {/* A bound value OUTSIDE the declared set (a binding that
                      predates a decl edit) must still display as the select's
                      value — without its own option the browser silently
                      renders the first declared option while state disagrees. */}
                  {!declared.includes(cur) && <option value={cur}>{cur}</option>}
                  {declared.map((s, i) => (
                    <option key={i} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              );
            })()
          ) : (
            <input
              aria-label={k.name}
              value={String(knobValues[k.name] ?? bound.get(k.name) ?? "")}
              onChange={(e) => set(k.name, e.target.value)}
            />
          )}
        </label>
      ))}
    </div>
  );
}
